
import { useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { MyContext } from '../../Context/MyProvider';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const Contact = () => {

    const { BaseUrl, supportEmail, supportEmailResponse, liveChatDays, liveChatHours, businessHoursDays, businessHoursTime } = useContext(MyContext);
    const { enqueueSnackbar } = useSnackbar();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [sending, setSending] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.message) {
            enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
            return;
        }
        setSending(true);
        try {
            const response = await axios.post(`${BaseUrl}/contact`, formData);
            if (response.data.status === 'success') {
                enqueueSnackbar('Message sent successfully! We\'ll get back to you soon.', { variant: 'success' });
                setFormData({ name: '', email: '', subject: '', message: '' });
            } else {
                enqueueSnackbar(response.data.message || 'Failed to send message. Please try again.', { variant: 'error' });
            }
        } catch (error) {
            console.error('[Contact Submit Error]:', error);
            enqueueSnackbar(error.response?.data?.message || 'Failed to send message. Please try again.', { variant: 'error' });
        } finally {
            setSending(false);
        }
    };

    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: (i = 0) => ({
            opacity: 1, y: 0,
            transition: { duration: 0.6, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] }
        })
    };

    const supportCards = [
        {
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
            ),
            title: 'Email Support',
            detail: supportEmail,
            sub: supportEmailResponse,
            color: '#4f46e5'
        },
        {
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    <path d="M8 10h.01" /><path d="M12 10h.01" /><path d="M16 10h.01" />
                </svg>
            ),
            title: 'Live Chat',
            detail: liveChatDays,
            sub: liveChatHours,
            color: '#7c3aed'
        },
        {
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 6v6l4 2" />
                    <circle cx="12" cy="12" r="10" />
                </svg>
            ),
            title: 'Business Hours',
            detail: businessHoursDays,
            sub: businessHoursTime,
            color: '#ec4899'
        }
    ];

    const faqItems = [
        { q: 'How do I get my API key?', a: 'Sign up for an account, navigate to your dashboard, and generate an API key from the "API Keys" section.' },
        { q: 'What payment methods do you accept?', a: 'We accept all major credit/debit cards, UPI, net banking, and wallet payments through our secure payment gateway.' },
        { q: 'Is there a free tier available?', a: 'Yes! We offer a free tier with limited API calls so you can test and explore our endpoints before upgrading.' },
        { q: 'How do I report a bug or issue?', a: `Use the contact form on this page, or email us directly at ${supportEmail} with details of the issue.` }
    ];

    const [openFaq, setOpenFaq] = useState(null);

    return (
        <div className="contact_page">

            {/* ── Hero Section ── */}
            <div className="contact-hero">
                <div className="contact-hero-bg"></div>
                <div className="contact-hero-stars"></div>
                <div className="width_container px-4 relative z-10">
                    <motion.div
                        className="text-center pt-[165px] pb-[160px]"
                        initial="hidden"
                        animate="visible"
                        variants={fadeUp}
                    >
                        <motion.span
                            variants={fadeUp}
                            custom={0}
                            className="inline-block px-5 py-2 rounded-full text-[13px] font-semibold tracking-wider uppercase mb-6"
                            style={{ background: 'rgba(79,70,229,0.15)', color: '#a78bfa', border: '1px solid rgba(79,70,229,0.3)' }}
                        >
                            Get in Touch
                        </motion.span>
                        <motion.h1
                            variants={fadeUp}
                            custom={1}
                            className="text-white font-bold text-[38px] sm:text-[52px] leading-tight mb-5"
                        >
                            Contact & <span style={{ background: 'linear-gradient(135deg, #818cf8, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Support</span>
                        </motion.h1>
                        <motion.p
                            variants={fadeUp}
                            custom={2}
                            className="text-gray-400 text-[16px] sm:text-[18px] max-w-[600px] mx-auto leading-relaxed"
                        >
                            Have a question, feedback, or need help? We're here for you.
                            Reach out and our team will get back to you promptly.
                        </motion.p>
                    </motion.div>
                </div>
            </div>

            {/* ── Support Cards ── */}
            <div className="width_container px-4 contact-cards-container relative z-20">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-[900px] mx-auto">
                    {supportCards.map((card, i) => (
                        <motion.div
                            key={i}
                            variants={fadeUp}
                            custom={i}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            className="contact-support-card"
                        >
                            <div className="contact-card-icon" style={{ background: `${card.color}15`, color: card.color }}>
                                {card.icon}
                            </div>
                            <h3 className="font-bold text-[17px] text-gray-900 mt-4">{card.title}</h3>
                            <p className="text-[15px] text-[#4f46e5] font-semibold mt-1">{card.detail}</p>
                            <p className="text-[13px] text-gray-500 mt-1">{card.sub}</p>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* ── Contact Form + Map Section ── */}
            <div className="width_container px-4 py-[80px]">
                <div className="flex max-_770_:flex-col gap-[40px] max-w-[1000px] mx-auto items-start">

                    {/* Form */}
                    <motion.div
                        className="w-full _770_:w-[55%]"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                        custom={0}
                    >
                        <h2 className="text-[28px] sm:text-[32px] font-bold text-gray-900 mb-2">Send us a Message</h2>
                        <p className="text-gray-500 text-[15px] mb-8">Fill out the form below and we'll respond within 24 hours.</p>

                        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                            <div className="flex max-_426_:flex-col gap-4">
                                <div className="contact-field flex-1">
                                    <label>Full Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="John Doe"
                                        required
                                    />
                                </div>
                                <div className="contact-field flex-1">
                                    <label>Email <span className="text-red-500">*</span></label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="you@company.com"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="contact-field">
                                <label>Subject</label>
                                <input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    placeholder="How can we help?"
                                />
                            </div>
                            <div className="contact-field">
                                <label>Message <span className="text-red-500">*</span></label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Tell us more about your inquiry..."
                                    rows="5"
                                    required
                                ></textarea>
                            </div>
                            <button
                                type="submit"
                                disabled={sending}
                                className="contact-submit-btn"
                            >
                                {sending ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                        </svg>
                                        Sending...
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center gap-2">
                                        Send Message
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="m22 2-7 20-4-9-9-4z" /><path d="M22 2 11 13" />
                                        </svg>
                                    </span>
                                )}
                            </button>
                        </form>
                    </motion.div>

                    {/* Side Info */}
                    <motion.div
                        className="w-full _770_:w-[45%]"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                        custom={1}
                    >
                        <div className="contact-info-box">
                            <div className="contact-info-header">
                                <h3 className="text-white font-bold text-[20px]">Quick Help</h3>
                                <p className="text-gray-300 text-[14px] mt-1">Common questions answered</p>
                            </div>
                            <div className="contact-info-body">
                                {faqItems.map((item, i) => (
                                    <div key={i} className="contact-faq-item">
                                        <button
                                            onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                            className="contact-faq-question"
                                        >
                                            <span>{item.q}</span>
                                            <motion.svg
                                                animate={{ rotate: openFaq === i ? 180 : 0 }}
                                                transition={{ duration: 0.2 }}
                                                xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                            >
                                                <path d="m6 9 6 6 6-6" />
                                            </motion.svg>
                                        </button>
                                        <motion.div
                                            initial={false}
                                            animate={{
                                                height: openFaq === i ? 'auto' : 0,
                                                opacity: openFaq === i ? 1 : 0
                                            }}
                                            transition={{ duration: 0.25 }}
                                            className="overflow-hidden"
                                        >
                                            <p className="text-gray-500 text-[13px] leading-relaxed pb-3 px-1">{item.a}</p>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                            <div className="contact-info-footer">
                                <p className="text-gray-500 text-[13px]">Still need help?</p>
                                <a href="mailto:support@astrosphere.api" className="text-[#4f46e5] font-semibold text-[14px] hover:underline">
                                    Email our support team →
                                </a>
                            </div>
                        </div>
                    </motion.div>

                </div>
            </div>

        </div>
    );
};

export default Contact;
